import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Fade,
  alpha,
  Chip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import useApp from "../../store/contexts/AppContext";

// Keyframe animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

// Styled components
const WidgetContainer = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: alpha(theme.palette.common.white, 0.03),
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
  animation: `${slideIn} 0.6s ease-out`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.4)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const WidgetHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.02),
}));

const HeaderTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: alpha(theme.palette.common.white, 0.9),
  fontSize: "0.85rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: alpha(theme.palette.common.white, 0.6),
  width: 28,
  height: 28,
  transition: "all 0.3s ease",
  "&:hover": {
    color: theme.palette.common.white,
    backgroundColor: alpha(theme.palette.common.white, 0.1),
    transform: "rotate(90deg)",
  },
}));

const AdItem = styled(Box)(({ theme, index }) => ({
  padding: theme.spacing(2.5),
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.05)}`,
  animation: `${slideIn} 0.5s ease-out ${index * 0.1}s both`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: "translateX(6px)",
    "& img": {
      transform: "scale(1.08)",
    },
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

const AdImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: 120,
  objectFit: "cover",
  borderRadius: 12,
  marginBottom: theme.spacing(1.5),
  transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: alpha(theme.palette.common.white, 0.05),
}));

const AdTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.95rem",
  marginBottom: theme.spacing(0.75),
  color: theme.palette.common.white,
  letterSpacing: "0.01em",
  lineHeight: 1.3,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const AdDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: alpha(theme.palette.common.white, 0.6),
  marginBottom: theme.spacing(1.5),
  lineHeight: 1.5,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const CTALink = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  color: theme.palette.primary.light,
  fontSize: "0.8rem",
  fontWeight: 600,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    transform: "translateX(3px)",
  },
}));

const SponsoredBadge = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: "0.7rem",
  fontWeight: 700,
  backgroundColor: alpha(theme.palette.primary.main, 0.2),
  color: theme.palette.primary.light,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  animation: `${float} 3s ease-in-out infinite`,
}));

const AdWidget = ({ position = "sidebar", maxAds = 2, size = "medium" }) => {
  const {
    appState: { userData },
    getActiveAdsApiCall,
    recordAdViewApiCall,
    recordAdClickApiCall,
  } = useApp();

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(new Set());
  const fetchInitiatedRef = useRef(false);
  const hasFetchedRef = useRef(false); // Track if we've already fetched

  // Fetch active ads - NO dependencies to prevent infinite loop
  const fetchAds = useCallback(async () => {
    if (fetchInitiatedRef.current || hasFetchedRef.current) return;

    fetchInitiatedRef.current = true;
    hasFetchedRef.current = true;

    try {
      setLoading(true);
      const result = await getActiveAdsApiCall();

      if (result.success && result.data) {
        const adsData = Array.isArray(result.data)
          ? result.data
          : result.data.ads || [];

        const userAccountType = (
          userData?.account_type || "basic"
        ).toLowerCase();
        const filteredAds = adsData
          .filter((ad) => {
            const visiblePlans = ad.visible_to_plans || [
              "basic",
              "standard",
              "premium",
              "elite",
            ];
            return visiblePlans.includes(userAccountType);
          })
          .slice(0, maxAds);

        setAds(filteredAds);
      }
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
      fetchInitiatedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // EMPTY ARRAY - fetch once only

  // Record ad view
  const recordAdView = useCallback(
    async (adId) => {
      if (viewRecorded.has(adId)) return;

      const result = await recordAdViewApiCall(adId);
      if (result.success) {
        setViewRecorded((prev) => new Set([...prev, adId]));
      }
    },
    [viewRecorded, recordAdViewApiCall]
  );

  // Record ad click
  const handleAdClick = async (ad) => {
    await recordAdClickApiCall(ad.id);

    if (ad.click_url) {
      window.open(ad.click_url, "_blank", "noopener,noreferrer");
    }
  };

  // Record view on mount
  useEffect(() => {
    if (ads.length > 0 && !dismissed) {
      const timer = setTimeout(() => {
        ads.forEach((ad) => {
          recordAdView(ad.id);
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [ads, dismissed, recordAdView]);

  // Initial fetch - ONLY ONCE
  useEffect(() => {
    if (userData?.id) {
      fetchAds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]); // NO fetchAds in dependencies!

  if (loading || dismissed || ads.length === 0) {
    return null;
  }

  return (
    <Fade in={!dismissed} timeout={600}>
      <WidgetContainer elevation={0}>
        <WidgetHeader>
          <HeaderTitle>
            <TrendingUpIcon sx={{ fontSize: "1.1rem" }} />
            <span>Sponsored</span>
          </HeaderTitle>
          <CloseButton size="small" onClick={() => setDismissed(true)}>
            <CloseIcon fontSize="small" />
          </CloseButton>
        </WidgetHeader>

        <Box>
          {ads.map((ad, index) => (
            <AdItem key={ad.id} index={index} onClick={() => handleAdClick(ad)}>
              {ad.media?.[0] && (
                <AdImage
                  src={ad.media[0].file_path}
                  alt={ad.title}
                  loading="lazy"
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <AdTitle variant="subtitle2">{ad.title}</AdTitle>
                <SponsoredBadge label="Ad" size="small" />
              </Box>

              {ad.description && (
                <AdDescription>{ad.description}</AdDescription>
              )}

              {ad.click_url && (
                <CTALink>
                  <span>Learn More</span>
                  <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
                </CTALink>
              )}
            </AdItem>
          ))}
        </Box>
      </WidgetContainer>
    </Fade>
  );
};

export default AdWidget;
